import { Box, Collapse, Divider, IconButton, Link, Stack, Typography } from '@mui/material'
import { useState } from 'react'
import { AddIcon, ArrowForwardIcon, CheckIcon, RemoveIcon } from 'src/assets/icons'
import { useScrollIntoView } from 'src/hooks/useScrollIntoView'
import gearsAnimation from 'src/assets/lottie/gears-animated.json'
import Lottie from 'lottie-react'

type TypesOfTrustsProps = {
    link: {
        path: string
        label: string
        componentRef: React.MutableRefObject<HTMLDivElement | null>
    }
}

const TypesOfTrusts = ({ link }: TypesOfTrustsProps) => {
    const [imgHover, setImgHover] = useState(false)
    useScrollIntoView(link)
    const [expandedTabs, setExpandedTabs] = useState<number[]>([])

    const tabs = [
        {
            id: 1,
            question: <Typography className="inline-block text-[#364152] text_lg_semibold">Why should I join a Pooled Trust?</Typography>,
            answer: (
                <Typography>
                    Under current New York Medicaid law, income in excess of the monthly Medicaid allowance must first be spent down on home care or medical
                    expenses prior to receiving Medicaid coverage. Unfortunately, the guidelines do not exempt living expenses, such as rent, food, clothes, or
                    utility services when calculating an applicant&apos;s excess income. As a result, Medicaid recipients may not have a sufficient amount of
                    funds to maintain their standard monthly living expenses.
                </Typography>
            )
        },
        {
            id: 2,
            question: <Typography className="inline-block text-[#364152] text_lg_semibold">What are the terms of eligibility?</Typography>,
            answer: (
                <Box className="mt-4 flex max-w-[593px] flex-col gap-4">
                    <Box className="flex gap-3">
                        <ArrowForwardIcon className="w-[18px] text-[#9AA4B2]" />
                        <Typography className="text-[#4B5565]">Trust accounts must be established for the sole benefit of the disabled beneficiary.</Typography>
                    </Box>
                    <Box className="flex gap-3">
                        <ArrowForwardIcon className="w-[18px] text-[#9AA4B2]" />
                        <Typography className="text-[#4B5565]">
                            The beneficiary or authorized representative must submit a completed and signed joinder agreement. You can request information or a
                            joinder agreement by calling.
                        </Typography>
                    </Box>
                    <Box className="flex gap-3">
                        <ArrowForwardIcon className="w-[18px] text-[#9AA4B2]" />
                        <Typography className="text-[#4B5565]">A beneficiary must be disabled as defined by law.</Typography>
                    </Box>
                    <Box className="flex gap-3">
                        <ArrowForwardIcon className="w-[18px] text-[#9AA4B2]" />
                        <Typography className="text-[#4B5565]">Income deposited to the trust account must belong to the beneficiary.</Typography>
                    </Box>
                </Box>
            )
        },
        {
            id: 3,
            question: <Typography className="inline-block text-[#364152] text_lg_semibold">How do I join SCS Pooled Trust?</Typography>,
            answer: (
                <Typography>
                    In order to join our Pooled Trust the beneficiary or authorized representative must submit a completed and signed Joinder Agreement. For
                    information on how to receive an application or <br /> for assistance in applying online please reach out to our office at <br />
                    <b>
                        <a href="tel:800-540-6732" style={{ color: 'inherit' }}>
                            800-540-6732
                        </a>
                    </b>{' '}
                    or by emailing{' '}
                    <Link className="text-[16px] font-semibold tracking-[1px] text-[#3C78A4]" href="mailto:info@scstrusts.org">
                        info@scstrusts.org
                    </Link>
                </Typography>
            )
        }
    ]

    const handleExpand = (id: number) => {
        if (expandedTabs.includes(id)) setExpandedTabs(expandedTabs.filter((tab) => tab !== id))
        else setExpandedTabs([...expandedTabs, id])
    }

    return (
        <Box ref={link.componentRef} className="px-28 py-16  ltb_1300:px-12 tbmd:flex tbmd:flex-col tbmd:items-center tbmd:px-5">
            <Typography className="mb-7 font-[Barlow] text-[48px] font-bold text-[#47599B] tbmd:text-center">Types of Trust We Administer</Typography>
            <Box className="grid grid-cols-2 gap-8 tbmd:grid-cols-1">
                <Box className="max-w-[592px]">
                    <Box className="mb-6 w-fit rounded-3xl border-[1px] border-solid border-[#BDC5E7] bg-[#DEE2F3] px-3 py-2">
                        <Typography className="text-[#47599B] text_lg_semibold">Pooled Trusts</Typography>
                    </Box>
                    <Typography className="mb-3 text-[#202939] text_lg_semibold">
                        Pooled Trusts are a means to provide the benefits of a special needs trust, without having to set up and administer a separate trust.
                    </Typography>
                    <Typography className="mb-6 text-[#202939]">
                        A pooled trust is managed by a non for profit organization. The assets of each member in the pooled trust are pooled together for
                        investment purposes, thereby allowing for more stable investments and a more streamlined approach to administering the trust
                        disbursements. SCS Trust Services supports individuals in the administration of 1st & 3rd party pooled trusts.
                    </Typography>
                    <Box className="rounded-3xl border-[1px] border-solid border-[#DEE2F3] bg-[#EEF1F9] px-9">
                        <Stack divider={<Divider />}>
                            {tabs.map((tab, index) => (
                                <Box key={tab.id} className="flex flex-col justify-center py-6">
                                    <Box className="flex items-center justify-between">
                                        <Box>
                                            <Typography className="mr-4 inline-block text-[15px] font-bold tracking-[1.95px] text-[#9BA9DA]">
                                                {String(index + 1).padStart(2, '0')}
                                            </Typography>
                                            {tab.question}
                                        </Box>
                                        <IconButton onClick={() => handleExpand(tab.id)}>{expandedTabs.includes(tab.id) ? <RemoveIcon /> : <AddIcon />}</IconButton>
                                    </Box>
                                    <Collapse in={expandedTabs.includes(tab.id)}>{tab.answer}</Collapse>
                                </Box>
                            ))}
                        </Stack>
                    </Box>
                </Box>
                <Box className="mx-auto h-auto w-[550px] bg-[url('https://res.cloudinary.com/dae9ughdn/image/upload/f_webp,q_auto,w_820/scs/site/types-trust-bground_zupdxl.png')] bg-contain bg-no-repeat tbmd:hidden">
                    <img
                        onMouseEnter={() => setImgHover(true)}
                        onMouseLeave={() => setImgHover(false)}
                        style={{
                            WebkitTransition: '-webkit-transform 1s',
                            MozTransition: '-moz-transform 1s',
                            transition: 'transform 1s',
                            WebkitTransform: imgHover ? 'rotate(-14deg)' : 'initial',
                            transform: imgHover ? 'rotate(-14deg)' : 'initial'
                        }}
                        className="relative left-14 top-[100px] h-auto max-w-[376px]"
                        src="https://res.cloudinary.com/dae9ughdn/image/upload/f_webp,q_auto,w_640/scs/site/types-of-trust_ny3hdm.png"
                        loading="lazy"
                        decoding="async"
                        width={460}
                        height={351}
                        alt="types-of-trust"
                    />
                    <img
                        onMouseEnter={() => setImgHover(true)}
                        onMouseLeave={() => setImgHover(false)}
                        style={{
                            WebkitTransition: '-webkit-transform 1s',
                            MozTransition: '-moz-transform 1s',
                            transition: 'transform 1s',
                            WebkitTransform: imgHover ? 'rotate(9deg)' : 'initial',
                            transform: imgHover ? 'rotate(9deg)' : 'initial'
                        }}
                        className="relative bottom-[125px] left-[166px] h-auto max-w-[376px]"
                        src="https://res.cloudinary.com/dae9ughdn/image/upload/f_webp,q_auto,w_640/scs/site/types-of-trust-2_tmzccy.png"
                        loading="lazy"
                        decoding="async"
                        width={455}
                        height={358}
                        alt="types-of-trust 2"
                    />
                </Box>
            </Box>
            <Box className="mt-8 grid grid-cols-2 items-center gap-8 tbmd:grid-cols-1">
                <Lottie
                    className="h-auto max-w-[592px] bg-[url('https://res.cloudinary.com/dae9ughdn/image/upload/f_webp,q_auto,w_900/scs/site/gears-bg_sk2otl.png')] bg-contain bg-no-repeat tbmd:my-12"
                    animationData={gearsAnimation}
                    loop={true}
                />
                <Box className="max-w-[592px]">
                    <Box className="mb-8 w-fit rounded-3xl border-[1px] border-solid border-[#BDC5E7] bg-[#DEE2F3] px-3 py-2">
                        <Typography className="text-[#47599B] text_lg_semibold">Individual Special Needs Trust</Typography>
                    </Box>
                    <Typography className="mb-3 text-[#202939] text_lg_semibold">
                        SCS Trust Services administers 1st & 3rd party Individual special need trusts.
                    </Typography>
                    <Typography className="text-[#202939]">
                        Unlike a Pooled trust, an individual special needs trust is created for a specific individual with a disability. Individual special needs
                        trusts can be administered by a family member, friend, or SCS Trust Services.
                    </Typography>
                </Box>
            </Box>
            <Typography
                className="my-12 text-center font-[Barlow] text-[40px] font-bold"
                sx={{
                    background: 'linear-gradient(92deg, #3C78A4 0.29%, #9F66B3 87.19%)',
                    '-webkit-background-clip': 'text',
                    '-webkit-text-fill-color': 'transparent'
                }}
            >
                1st Party Trust vs. 3rd Party Trust
            </Typography>
            <Box className="flex justify-center gap-8 tbmd:flex-col">
                <Box className="max-w-[488px] rounded-3xl border-[1px] border-solid border-[#CDD5DF] bg-[#F8FAFC] p-6">
                    <Typography className="font-[Barlow] display_xs_semibold">1st Party</Typography>
                    <Divider className="my-6" />
                    <Box className="flex flex-col gap-9">
                        <Box className="flex items-center gap-4">
                            <Box className="flex h-6 w-6 items-center justify-center rounded-full bg-[#E1F3EE]">
                                <CheckIcon className="text-[16px] text-[#479F73]" />
                            </Box>
                            <Typography className="text-[#4B5565]">Protect the Assets of an individual with a disability</Typography>
                        </Box>
                        <Box className="flex items-center gap-4">
                            <Box className="flex h-6 w-6 items-center justify-center rounded-full bg-[#E1F3EE]">
                                <CheckIcon className="text-[16px] text-[#479F73]" />
                            </Box>
                            <Typography className="text-[#4B5565]">
                                Can only be established and funded by the disabled individual, their POA, or Guardian
                            </Typography>
                        </Box>
                        <Box className="flex items-center gap-4">
                            <Box className="flex h-6 w-6 items-center justify-center rounded-full bg-[#E1F3EE]">
                                <CheckIcon className="text-[16px] text-[#479F73]" />
                            </Box>
                            <Typography className="text-[#4B5565]">Subject to the Medicaid Payback</Typography>
                        </Box>
                    </Box>
                </Box>
                <Box className="max-w-[488px] rounded-3xl  border-[1px] border-solid border-[#CDD5DF] bg-[#F8FAFC] p-6">
                    <Typography className="font-[Barlow] display_xs_semibold">3rd Party</Typography>
                    <Divider className="my-6" />
                    <Box className="flex flex-col gap-9">
                        <Box className="flex items-center gap-4">
                            <Box className="flex h-6 w-6 items-center justify-center rounded-full bg-[#E1F3EE]">
                                <CheckIcon className="text-[16px] text-[#479F73]" />
                            </Box>
                            <Typography className="text-[#4B5565]">Protects the existing or future benefits of a person with a disability</Typography>
                        </Box>
                        <Box className="flex items-center gap-4">
                            <Box className="flex h-6 w-6 items-center justify-center rounded-full bg-[#E1F3EE]">
                                <CheckIcon className="text-[16px] text-[#479F73]" />
                            </Box>
                            <Typography className="text-[#4B5565]">Can be funded by a parent, grandparent, or a family member of a disabled individuals</Typography>
                        </Box>
                        <Box className="flex items-center gap-4">
                            <Box className="flex h-6 w-6 items-center justify-center rounded-full bg-[#F9EBF0]">
                                <RemoveIcon className="text-[16px] text-[#D65463]" />
                            </Box>
                            <Typography className="text-[#4B5565]">Not subject to the Medicaid Payback</Typography>
                        </Box>
                    </Box>
                </Box>
            </Box>
        </Box>
    )
}

export default TypesOfTrusts
